import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Helmet } from "@/components/Helmet";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error" | "invalid">("loading");

  useEffect(() => {
    const handleUnsubscribe = async () => {
      if (!token) {
        setStatus("invalid");
        return;
      }

      try {
        const { error, data } = await supabase.rpc("unsubscribe_newsletter", {
          _token: token,
        });

        if (error) {
          console.error("Unsubscribe error:", error);
          setStatus("error");
          return;
        }

        if (!data) {
          setStatus("invalid");
          return;
        }

        setStatus("success");
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : "Unknown error";
        console.error("Unsubscribe error:", message);
        setStatus("error");
      }
    };

    handleUnsubscribe();
  }, [token]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Unsubscribe | ShadowScan Newsletter</title>
        <meta
          name="description"
          content="Manage your ShadowScan newsletter preferences. Confirm removal from threat intelligence updates and blog email alerts with a quick, secure unsubscribe flow."
        />
      </Helmet>
      <Navigation />
      
      <main className="flex-1 flex items-center justify-center py-20 px-4">
        <div className="max-w-md w-full text-center">
          {status === "loading" && (
            <div className="space-y-4">
              <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
              <h1 className="text-2xl font-bold text-foreground">Processing...</h1>
              <p className="text-muted-foreground">Please wait while we update your preferences.</p>
            </div>
          )}

          {status === "success" && (
            <div className="space-y-4">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">Unsubscribed Successfully</h1>
              <p className="text-muted-foreground">
                You've been removed from our newsletter. You won't receive any more blog notification emails.
              </p>
              <Button asChild className="mt-6">
                <Link to="/">Return to Homepage</Link>
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="space-y-4">
              <XCircle className="h-16 w-16 text-destructive mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">Something Went Wrong</h1>
              <p className="text-muted-foreground">
                We couldn't process your unsubscribe request. Please try again or contact support.
              </p>
              <Button asChild className="mt-6">
                <Link to="/contact">Contact Support</Link>
              </Button>
            </div>
          )}

          {status === "invalid" && (
            <div className="space-y-4">
              <XCircle className="h-16 w-16 text-muted-foreground mx-auto" />
              <h1 className="text-2xl font-bold text-foreground">Invalid Link</h1>
              <p className="text-muted-foreground">
                This unsubscribe link is invalid or has already been used.
              </p>
              <Button asChild className="mt-6">
                <Link to="/">Return to Homepage</Link>
              </Button>
            </div>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default Unsubscribe;
